import { useContext } from "react";
import { FRAME, GROUP_WIDGET, SHAPE, TEXT, SCOPE_OF_WORK } from "../constants/editor";
import { EditorContext } from "../containers/editor/EditorLayout";
import {
  getCssTransformObj,
  getCssTransformObjToString,
  getRandomString,
  replaceDuplicateMfIdsFromText,
} from "../_helpers/utils";
import useSelectable from "./useSelectable";
import useReplaceDuplicateShapeId from "./useReplaceDuplicateShapeId";
import useSetDimension from "./useSetDimension";
import { EditorPanelContext } from "../containers/editor/EditorPanel";
import useReplaceScopeOfWorkIds from "./useReplaceScopeOfWorkIds";

const useCloneWidget = () => {
  let {
    metadata,
    widgets,
    updateMetadata,
    updateWidgets,
    audios,
    setAudios,
    isTimeLineViewOpen,
    setIsTimeLineViewOpen,
    dimension,
    clonedObjRef,
    setClonedObjRef,
  } = useContext(EditorContext);
  let { keyBoardEvent, updateKeyBoardEvent } = useContext(EditorPanelContext);

  const { hideAllWidgetSelection } = useSelectable();
  const { replaceDuplicateIdFromSVG, repalceDuplicateIdFromFrame } = useReplaceDuplicateShapeId();
  const { setAudioDurationBasedOnPageDuration } = useSetDimension();
  const replaceDuplicateIdFromScopeOfWork = useReplaceScopeOfWorkIds();

  const getBoundary = targetObj => {
    let minLeft = document.getElementById(targetObj[0]).getBoundingClientRect().left;
    let minTop = document.getElementById(targetObj[0]).getBoundingClientRect().top;

    targetObj.forEach(id => {
      let x = document.getElementById(id).getBoundingClientRect().left;
      let y = document.getElementById(id).getBoundingClientRect().top;
      if (x < minLeft) minLeft = x;
      if (y < minTop) minTop = y;
    });

    let boundary = {
      x: minLeft * (100 / dimension.zoom),
      y: minTop * (100 / dimension.zoom),
    };

    return boundary;
  };

  // Clone widget during keyboard event ctrl+d or clone button clicked
  const cloneWidget = () => {
    if (!metadata.activeWidgetId) return;

    let updatedwidgetArray = [];
    let updatedWidgetId = [];
    let updatedWidgetAssetType = [];
    let targetWidgetIds = [];
    let targetWidgetIndexes = [];
    let childIdRefs = {};

    for (let i = 0; i < metadata.activeWidgetId.length; i++) {
      let id = metadata.activeWidgetId[i];
      let isGroupWidget = document.getElementById(id).closest(".dhp-page-group");
      let targetId = isGroupWidget ? document.getElementById(id).closest(".dhp-root-widget").getAttribute("id") : id;
      targetWidgetIds.push(targetId);
      targetWidgetIndexes.push(widgets.findIndex(widget => widget.id === targetId));
    }

    targetWidgetIndexes.sort().map(targetWidgetIndex => {
      //Copy Widget
      let cloneObj = widgets[targetWidgetIndex];

      //Paste Widget
      let targetDOM;
      let clonedObjAssetType = cloneObj.data["data-asset-type"];
      let newWidgetId =
        clonedObjAssetType !== GROUP_WIDGET ? "dhp-widget-" + getRandomString(8) : "dhp-group-" + getRandomString(8);
      let widgetTransform = cloneObj.style.transform;
      let widgettranslateValue = getCssTransformObj({ transform: widgetTransform, exclude: ["rotate", "scale"] });

      // calculate delta with respect to previous cloned obj ref
      let deltaX = 25;
      let deltaY = 25;
      if (clonedObjRef) {
        // for single or group widget clone
        if (clonedObjRef.sourceObj.length === 1) {
          let sourceWidgetTransform = getCssTransformObj({
            transform: document.getElementById(clonedObjRef.sourceObj).style.transform,
            exclude: ["rotate", "scale"],
          });
          let destWidgetTransform = getCssTransformObj({
            transform: document.getElementById(clonedObjRef.destObj).style.transform,
            exclude: ["rotate", "scale"],
          });

          deltaX = parseFloat(destWidgetTransform.translate.x) - parseFloat(sourceWidgetTransform.translate.x);
          deltaY = parseFloat(destWidgetTransform.translate.y) - parseFloat(sourceWidgetTransform.translate.y);
        }
        // for multi widget clone
        else {
          let sourceObjBoundary = getBoundary(clonedObjRef.sourceObj);
          let destObjBoundary = getBoundary(clonedObjRef.destObj);
          deltaX = parseFloat(destObjBoundary.x) - parseFloat(sourceObjBoundary.x);
          deltaY = parseFloat(destObjBoundary.y) - parseFloat(sourceObjBoundary.y);
        }

        // check if widget goes outside set delta to default 25
        let widgetLeft = parseFloat(widgettranslateValue.translate.x) + deltaX;
        let widgetTop = parseFloat(widgettranslateValue.translate.y) + deltaY;

        if (deltaX > 0) {
          if (widgetLeft > parseFloat(document.getElementById(metadata.activeBlockId).style.width)) deltaX = 25;
        } else {
          let widgetRight = widgetLeft + cloneObj.style.width;
          if (widgetRight < 0) deltaX = 25;
        }
        if (deltaY > 0) {
          if (widgetTop > parseFloat(document.getElementById(metadata.activeBlockId).style.height)) deltaY = 25;
        } else {
          let widgetBottom = widgetTop + cloneObj.style.height;
          if (widgetBottom < 0) deltaY = 25;
        }
      }

      let widgetTransformObj = getCssTransformObj({
        transform: widgetTransform,
        translateX: `${parseFloat(widgettranslateValue.translate.x) + deltaX}px`,
        translateY: `${parseFloat(widgettranslateValue.translate.y) + deltaY}px`,
      });
      let widgetTransformString = getCssTransformObjToString({ transformObj: widgetTransformObj });

      // update innerWidgtes id if a group widget is cloned
      if (clonedObjAssetType === GROUP_WIDGET) {
        let copiedObjIneerHtml = cloneObj.innerHTML;
        targetDOM = document.createElement("div");
        targetDOM.innerHTML = copiedObjIneerHtml;
        targetDOM.querySelectorAll(".dhp-page-widget").forEach(element => {
          const newId = "dhp-widget-" + getRandomString(8);
          childIdRefs = { ...childIdRefs, [element.id]: newId };
          element.setAttribute("id", newId);
        });
        targetDOM.querySelectorAll(".dhp-page-group").forEach(element => {
          element.setAttribute("id", "dhp-group-" + getRandomString(8));
        });
        replaceDuplicateIdFromSVG(targetDOM);
        replaceDuplicateMfIdsFromText(targetDOM);
      }
      // Replace duplicate id for shape use in shape border
      if (
        clonedObjAssetType === SHAPE &&
        (cloneObj.data["data-border-style"] === "solid" || cloneObj.data["data-grad-scolor"])
      ) {
        let copiedObjIneerHtml = cloneObj.innerHTML;
        targetDOM = document.createElement("div");
        targetDOM.innerHTML = copiedObjIneerHtml;

        replaceDuplicateIdFromSVG(targetDOM, cloneObj);
      }
      // Replace duplicate id from merge fields used in text widgets
      if (clonedObjAssetType === TEXT) {
        let copiedObjIneerHtml = cloneObj.innerHTML;
        targetDOM = document.createElement("div");
        targetDOM.innerHTML = copiedObjIneerHtml;

        replaceDuplicateMfIdsFromText(targetDOM);
      }
      // Replace duplicate id for frame
      if (clonedObjAssetType === FRAME) {
        let copiedObjIneerHtml = cloneObj.innerHTML;
        let targetDOM = document.createElement("div");
        targetDOM.innerHTML = copiedObjIneerHtml;

        repalceDuplicateIdFromFrame(targetDOM, cloneObj);
        // cloneObj.innerHTML = targetDOM.innerHTML;
      }
      // Replace or concatenate the input check box id
      if (clonedObjAssetType === SCOPE_OF_WORK) {
        let copiedObjInnerHtml = cloneObj.innerHTML;
        let targetDOM = document.createElement("div");
        targetDOM.innerHTML = copiedObjInnerHtml;

        replaceDuplicateIdFromScopeOfWork(targetDOM);

        widgets[targetWidgetIndex] = {
          ...widgets[targetWidgetIndex],
          innerHTML: targetDOM.innerHTML,
        };
      }

      let updatedClipboardObj = {
        ...cloneObj,
        pageId: metadata.activePageId,
        blockId: metadata.activeBlockId,
        id: newWidgetId,
        style: { ...cloneObj.style, transform: widgetTransformString },
        data: {
          ...cloneObj.data,
          "data-x-allignment": false,
          "data-y-allignment": false,
        },
        innerHTML: targetDOM ? targetDOM.innerHTML : cloneObj.innerHTML,
      };

      if (updatedClipboardObj?.groupSrcSet) {
        let newGroupSrcSet = {};
        Object.keys(updatedClipboardObj?.groupSrcSet)?.forEach(oldId => {
          if (childIdRefs[oldId])
            newGroupSrcSet = { ...newGroupSrcSet, [childIdRefs[oldId]]: updatedClipboardObj?.groupSrcSet?.[oldId] };
        });
        updatedClipboardObj.groupSrcSet = newGroupSrcSet;
      }

      updatedwidgetArray.push(updatedClipboardObj);
      updatedWidgetId.push(newWidgetId);
      updatedWidgetAssetType.push(cloneObj.data["data-asset-type"]);
    });

    setClonedObjRef({
      sourceObj: targetWidgetIds,
      destObj: updatedWidgetId,
    });

    updateWidgets([...widgets, ...updatedwidgetArray]);

    // Note : timeout 30 is given for - handler is not move to new cloned widget for keyboard event (For multi selct widget) (removed now)
    sessionStorage.setItem("retainRotation", false);
    updateMetadata({
      ...metadata,
      activeWidgetId: updatedWidgetId,
      activeWidgetType: updatedWidgetAssetType,
    });
    updateKeyBoardEvent({ ...keyBoardEvent, clone: false });
    hideAllWidgetSelection({ exceptions: updatedWidgetId, type: "group" });
  };

  // Clone widget during page or block clone
  const cloneBlockWidgets = (
    targetBlockId,
    currentBlockId,
    currentBlockIdx,
    currentPageId,
    currentPageIdx,
    reindexedPageArray,
    reindexedBlockArray,
    updatedBackgroundColorObj,
    updatedBackgroundImageObj
  ) => {
    let updatedwidgetArray = [];
    let targetWidgetIndexes = [];
    let linkedWidgetIncList = [];
    let newArray = Object.assign([...widgets]);
    let childIdRefs = {};

    // get the widget list which are linked with the block appear after added block
    document.querySelectorAll(".dhp-widget-hyperlinked").forEach(element => {
      if (
        parseInt(element.getAttribute("data-hyperlink-url")) > parseInt(metadata.activeBlockIdx) &&
        element.getAttribute("id")
      ) {
        linkedWidgetIncList.push(element.getAttribute("id"));
      }
    });

    //update links in widget array
    linkedWidgetIncList.forEach(id => {
      let isGroupWidget = document.getElementById(id).closest(".dhp-page-group");
      let targetId = isGroupWidget ? document.getElementById(id).closest(".dhp-root-widget").getAttribute("id") : id;
      let targetWidgetIndex = widgets.findIndex(widget => widget.id === targetId);
      let linkedPageIdx = parseInt(document.getElementById(id).getAttribute("data-hyperlink-url"));

      if (isGroupWidget) {
        document.getElementById(id).setAttribute("data-hyperlink-url", linkedPageIdx + 1);

        newArray = Object.assign([...newArray], {
          [targetWidgetIndex]: {
            ...widgets[targetWidgetIndex],
            innerHTML: document.getElementById(targetId).innerHTML,
          },
        });
      } else {
        newArray = Object.assign([...newArray], {
          [targetWidgetIndex]: {
            ...widgets[targetWidgetIndex],
            data: {
              ...widgets[targetWidgetIndex].data,
              "data-hyperlink-url": linkedPageIdx + 1,
            },
          },
        });
      }
    });

    let clonedWidgetArray = newArray.filter(widget => widget.blockId === targetBlockId);

    for (let i = 0; i < clonedWidgetArray.length; i++) {
      targetWidgetIndexes.push(newArray.findIndex(widget => widget.id === clonedWidgetArray[i].id));
    }

    targetWidgetIndexes.map(targetWidgetIndex => {
      //Copy Widget
      let cloneObj = newArray[targetWidgetIndex];
      let targetDOM;
      let clonedObjAssetType = cloneObj.data["data-asset-type"];
      let newWidgetId =
        clonedObjAssetType !== GROUP_WIDGET ? "dhp-widget-" + getRandomString(8) : "dhp-group-" + getRandomString(8);

      // update innerWidgtes id if a group widget is cloned
      if (clonedObjAssetType === GROUP_WIDGET) {
        let copiedObjIneerHtml = cloneObj.innerHTML;
        targetDOM = document.createElement("div");
        targetDOM.innerHTML = copiedObjIneerHtml;
        targetDOM.querySelectorAll(".dhp-page-widget").forEach(element => {
          const newId = "dhp-widget-" + getRandomString(8);
          childIdRefs = { ...childIdRefs, [element.id]: newId };
          element.setAttribute("id", newId);
          element.classList.remove(".group-selected");
        });
        targetDOM.querySelectorAll(".dhp-page-group").forEach(element => {
          element.setAttribute("id", "dhp-group-" + getRandomString(8));
          element.classList.remove(".group-selected");
        });
        replaceDuplicateIdFromSVG(targetDOM);
        replaceDuplicateMfIdsFromText(targetDOM);
      }
      // Replace duplicate id for shape use in shape border
      if (
        clonedObjAssetType === SHAPE &&
        (cloneObj.data["data-border-style"] === "solid" || cloneObj.data["data-grad-scolor"])
      ) {
        let copiedObjIneerHtml = cloneObj.innerHTML;
        targetDOM = document.createElement("div");
        targetDOM.innerHTML = copiedObjIneerHtml;

        replaceDuplicateIdFromSVG(targetDOM, cloneObj);
      }
      // Replace duplicate id from merge fields used in text widgets
      if (clonedObjAssetType === TEXT) {
        let copiedObjIneerHtml = cloneObj.innerHTML;
        targetDOM = document.createElement("div");
        targetDOM.innerHTML = copiedObjIneerHtml;

        replaceDuplicateMfIdsFromText(targetDOM);
      }
      // Replace duplicate id for frame
      if (clonedObjAssetType === FRAME) {
        let copiedObjIneerHtml = cloneObj.innerHTML;
        let targetDOM = document.createElement("div");
        targetDOM.innerHTML = copiedObjIneerHtml;

        repalceDuplicateIdFromFrame(targetDOM, cloneObj);
        // cloneObj.innerHTML = targetDOM.innerHTML;
      }
      if (clonedObjAssetType === SCOPE_OF_WORK) {
        let copiedObjInnerHtml = cloneObj.innerHTML;
        let targetDOM = document.createElement("div");
        targetDOM.innerHTML = copiedObjInnerHtml;

        replaceDuplicateIdFromScopeOfWork(targetDOM);
        newArray[targetWidgetIndex] = {
          ...newArray[targetWidgetIndex],
          innerHTML: targetDOM.innerHTML,
        };
      }

      //Paste Widget
      let updatedwidgetObj = {
        ...cloneObj,
        pageId: currentPageId,
        blockId: currentBlockId,
        id: newWidgetId,
        style: { ...cloneObj.style },
        innerHTML: targetDOM ? targetDOM.innerHTML : cloneObj.innerHTML,
      };

      if (updatedwidgetObj?.groupSrcSet) {
        let newGroupSrcSet = {};
        Object.keys(updatedwidgetObj?.groupSrcSet)?.forEach(oldId => {
          if (childIdRefs[oldId])
            newGroupSrcSet = { ...newGroupSrcSet, [childIdRefs[oldId]]: updatedwidgetObj?.groupSrcSet?.[oldId] };
        });
        updatedwidgetObj.groupSrcSet = newGroupSrcSet;
      }

      updatedwidgetArray.push(updatedwidgetObj);
    });

    updateMetadata({
      ...metadata,
      activePageId: reindexedPageArray ? currentPageId : metadata.activePageId,
      activePageIdx: reindexedPageArray ? currentPageIdx : metadata.activePageIdx,
      activeBlockId: currentBlockId,
      activeBlockIdx: currentBlockIdx,
      activeWidgetId: false,
      activeWidgetType: false,
      activeOutSideCanvasArea: false,
      disableAutoScroll: false,
    });

    //audios which have same end time like doc duration, update thet audio's end time also as page duration time update
    if (audios?.length > 0) {
      let newAudioArray = setAudioDurationBasedOnPageDuration(
        reindexedPageArray[currentPageIdx].pageDuration,
        "clone-page"
      );

      updateWidgets(
        [...newArray, ...updatedwidgetArray],
        reindexedPageArray,
        reindexedBlockArray,
        updatedBackgroundColorObj,
        updatedBackgroundImageObj,
        currentBlockIdx,
        false,
        false,
        false,
        newAudioArray
      );
    } else
      updateWidgets(
        [...newArray, ...updatedwidgetArray],
        reindexedPageArray,
        reindexedBlockArray,
        updatedBackgroundColorObj,
        updatedBackgroundImageObj,
        currentBlockIdx
      );
  };

  // Clone audio during keyboard event ctrl+d or clone button clicked
  const cloneAudio = () => {
    let updatedAudioArray = [];
    let targetAudioIndexes = audios.findIndex(audio => audio.id === metadata.activeAudioId);
    let cloneObj = audios[targetAudioIndexes];
    let newAudioId = "dhp-audio-" + getRandomString(8);

    let updatedAudioObj = {
      ...cloneObj,
      id: newAudioId,
    };
    updatedAudioArray.push(updatedAudioObj);

    setAudios([...audios, ...updatedAudioArray]);
    updateMetadata({
      ...metadata,
      activeAudioId: newAudioId,
    });

    if (isTimeLineViewOpen) {
      if (isTimeLineViewOpen === "page-timeline") {
        setIsTimeLineViewOpen("document-timeline"); // when insert any audio if page timeline vieww is open switch to documnet timeline
      }

      // scroll to last added audio if it is not in viewport
      setTimeout(() => {
        document.getElementById(newAudioId).scrollIntoView();
      }, 100);
    }
  };

  return { cloneWidget, cloneBlockWidgets, cloneAudio };
};

export default useCloneWidget;
